/**
 * SEO helpers: exact per-route meta, absolute URLs and schema.org JSON-LD builders.
 * Visual design is untouched; everything here only feeds <head>.
 */

export const SITE_NAME = 'Bukit Ancak Lembongan Resort';
export const DEFAULT_SITE_URL = 'https://bukitancaklembongan.com';

export const ROUTE_META = {
  '/': {
    title: 'Bukit Ancak Lembongan | Hillside Resort, Nusa Lembongan',
    description: 'Nine sea-view deluxe rooms on Ancak hill, overlooking the Yellow Bridge and Nusa Ceningan. Boutique resort in Nusa Lembongan, Bali. Book direct for best rates.',
    crumb: 'Home',
  },
  '/stay': {
    title: 'Deluxe Sea-View Rooms | Bukit Ancak Lembongan',
    description: 'Nine Deluxe Rooms facing the channel and the Yellow Bridge. Wake to sunrise over Nusa Ceningan. See room details, amenities and book direct at Bukit Ancak.',
    crumb: 'Stay',
  },
  '/dining': {
    title: 'Ancak Table & Bar | Dining in Nusa Lembongan',
    description: 'Island produce, Balinese spice and sunset cocktails with Yellow Bridge views. Breakfast to late evening at Ancak Table and Bar, Bukit Ancak Lembongan.',
    crumb: 'Dining',
  },
  '/experience': {
    title: 'Things to Do in Nusa Lembongan | Bukit Ancak',
    description: "Snorkel with mantas, canoe the mangroves, see Devil's Tear and walk the Yellow Bridge at sunrise. Island experiences arranged by Bukit Ancak Lembongan.",
    crumb: 'Experience',
  },
  '/offers': {
    title: 'Special Offers & Direct Deals | Bukit Ancak Lembongan',
    description: 'Book direct and save with early-bird and seasonal offers at Bukit Ancak Lembongan Resort, a hillside retreat above the Yellow Bridge, Nusa Lembongan.',
    crumb: 'Offers',
  },
  '/journal': {
    title: 'Journal | Nusa Lembongan Travel Stories & Tips',
    description: 'Island guides, travel tips and stories from Nusa Lembongan and Nusa Ceningan, written from the hill at Bukit Ancak Lembongan Resort.',
    crumb: 'Journal',
  },
  '/gallery': {
    title: 'Gallery | Bukit Ancak Lembongan Resort',
    description: 'See the rooms, views, dining and sunsets at Bukit Ancak Lembongan, a nine-room hillside resort above the Yellow Bridge in Nusa Lembongan, Bali.',
    crumb: 'Gallery',
  },
  '/getting-here': {
    title: 'Getting Here & Boat Schedule | Bukit Ancak Lembongan',
    description: 'How to reach Bukit Ancak: airport to Sanur, daily fast boat timetable Sanur to Nusa Lembongan by operator, beach pick-up and travel tips. Book your boat via WhatsApp.',
    crumb: 'Getting here',
  },
  '/contact': {
    title: 'Contact & Getting Here | Bukit Ancak Lembongan',
    description: 'Reach Bukit Ancak in 30 minutes by fast boat from Sanur to Nusa Lembongan. Call +62 821-3761-249 or email info@bukitancaklembongan.com.',
    crumb: 'Contact',
  },
};

export const siteUrlOf = (settings) => ((settings?.site_url || DEFAULT_SITE_URL).trim().replace(/\/+$/, ''));

/** Absolute URL on the canonical host (uploads live under /api/uploads on the same host). */
export const absUrl = (settings, url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${siteUrlOf(settings)}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const canonicalFor = (settings, path) => {
  const clean = (path || '/').split('?')[0].split('#')[0].replace(/\/+$/, '');
  return `${siteUrlOf(settings)}${clean || ''}`;
};

export const truncate = (s, max) => {
  const t = (s || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
};

/** 140-160 character description from an excerpt / body text. */
export const metaDescription = (primary, fallbackBody = '') => {
  const strip = (h) => (h || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  let d = strip(primary) || strip(fallbackBody);
  if (d.length > 160) d = truncate(d, 160);
  if (d.length < 140) {
    const pad = ' Read the full story on the Bukit Ancak Journal, Nusa Lembongan, Bali.';
    d = truncate(`${d}${pad}`, 160);
  }
  return d;
};

export const telHref = (phone) => `tel:+${(phone || '').replace(/[^0-9]/g, '')}`;
export const waHref = (number) => `https://wa.me/${(number || '').replace(/[^0-9]/g, '')}`;

/** One source of truth for the guest rating shown in the hero and the reviews section. */
export const reviewStats = (reviews = []) => {
  const count = reviews.length;
  if (!count) return { count: 0, avg: 0, avgLabel: '0.0' };
  const avg = reviews.reduce((a, r) => a + (r.rating_scale === 10 ? r.rating / 2 : r.rating), 0) / count;
  return { count, avg, avgLabel: avg.toFixed(1) };
};

export const isStockPhoto = (url = '') => /unsplash\.com|pexels\.com|pixabay\.com/i.test(url);

// ---------- schema.org ----------
const org = (settings) => ({
  '@type': 'Organization',
  name: settings?.operator_name || 'PT Armella Management Indonesia',
  url: settings?.operator_url || 'https://armellabali.com',
});

export const resortSchema = (site, { withRating } = {}) => {
  const s = site?.settings || {};
  const base = siteUrlOf(s);
  const photos = [];
  (site?.hero?.slides || []).slice(0, 3).forEach((x) => photos.push(absUrl(s, x.url)));
  (site?.rooms || []).forEach((r) => (r.photos || []).slice(0, 2).forEach((p) => photos.push(absUrl(s, p.url))));
  (site?.outlets || []).forEach((o) => (o.images || []).slice(0, 1).forEach((u) => photos.push(absUrl(s, u))));
  const images = [...new Set(photos.filter(Boolean))].slice(0, 6);
  const sameAs = [s.instagram, s.facebook, s.tiktok, s.tripadvisor_url, s.google_reviews_url].filter(Boolean);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Resort',
    '@id': `${base}/#resort`,
    name: s.site_name || SITE_NAME,
    description: ROUTE_META['/'].description,
    url: base,
    logo: `${base}/logos/bukit-ancak.png`,
    image: images.length ? images : [`${base}/og-default.jpg`],
    telephone: s.phone ? `+${s.phone.replace(/[^0-9]/g, '')}` : undefined,
    email: s.email || undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: s.address || undefined,
      addressLocality: s.address_locality || 'Nusa Lembongan',
      addressRegion: s.address_region || 'Bali',
      postalCode: s.postal_code || '80771',
      addressCountry: s.country_code || 'ID',
    },
    // Only published once the admin confirms the pin is the exact property location (Settings -> SEO)
    geo: s.geo_exact && s.map_lat && s.map_lng ? { '@type': 'GeoCoordinates', latitude: s.map_lat, longitude: s.map_lng } : undefined,
    priceRange: s.price_range || '$$',
    numberOfRooms: 9,
    checkinTime: s.checkin_time || '14:00',
    checkoutTime: s.checkout_time || '12:00',
    amenityFeature: (s.amenities || []).map((a) => ({ '@type': 'LocationFeatureSpecification', name: a, value: true })),
    sameAs: sameAs.length ? sameAs : undefined,
    parentOrganization: org(s),
    potentialAction: s.reserve_url ? { '@type': 'ReserveAction', target: { '@type': 'EntryPoint', urlTemplate: s.reserve_url, actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform'] }, result: { '@type': 'LodgingReservation', name: 'Room reservation' } } : undefined,
  };
  if (withRating && withRating.count > 0) {
    schema.aggregateRating = { '@type': 'AggregateRating', ratingValue: withRating.avgLabel, bestRating: '5', reviewCount: String(withRating.count) };
  }
  return prune(schema);
};

export const breadcrumbSchema = (settings, items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: canonicalFor(settings, it.path) })),
});

export const blogPostingSchema = (site, post) => {
  const s = site?.settings || {};
  const base = siteUrlOf(s);
  const url = canonicalFor(s, `/journal/${post.slug}`);
  return prune({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: truncate(post.title, 110),
    description: metaDescription(post.seo_description || post.excerpt, post.body),
    image: post.cover ? [absUrl(s, post.cover)] : [`${base}/og-default.jpg`],
    datePublished: post.date || post.created_at,
    dateModified: post.updated_at || post.date || post.created_at,
    author: { '@type': 'Organization', name: s.site_name || SITE_NAME, url: base },
    publisher: { '@type': 'Organization', name: s.site_name || SITE_NAME, logo: { '@type': 'ImageObject', url: `${base}/logos/bukit-ancak.png` } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  });
};

export const restaurantSchema = (site, outlet) => {
  const s = site?.settings || {};
  const base = siteUrlOf(s);
  return prune({
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    '@id': `${base}/dining#restaurant`,
    name: outlet?.name || 'Ancak Table and Bar',
    description: ROUTE_META['/dining'].description,
    url: `${base}/dining`,
    image: (outlet?.images || []).slice(0, 3).map((u) => absUrl(s, u)),
    servesCuisine: ['Indonesian', 'Balinese', 'International'],
    telephone: s.phone ? `+${s.phone.replace(/[^0-9]/g, '')}` : undefined,
    priceRange: s.price_range || '$$',
    containedInPlace: { '@type': 'Resort', '@id': `${base}/#resort`, name: s.site_name || SITE_NAME },
    hasMenu: outlet?.menu_pdf ? absUrl(s, outlet.menu_pdf) : undefined,
    address: { '@type': 'PostalAddress', addressLocality: 'Nusa Lembongan', addressRegion: 'Bali', postalCode: '80771', addressCountry: 'ID' },
  });
};

export const offersSchema = (site, offers = []) => {
  const s = site?.settings || {};
  const base = siteUrlOf(s);
  return prune({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Special offers at Bukit Ancak Lembongan',
    url: `${base}/offers`,
    itemListElement: offers.map((o, i) => prune({
      '@type': 'ListItem',
      position: i + 1,
      item: prune({
        '@type': 'Offer',
        name: o.title,
        description: (o.description || o.subtitle || '').replace(/\s+/g, ' ').trim().slice(0, 300) || undefined,
        validFrom: o.valid_from || undefined,
        validThrough: o.valid_until || undefined,
        url: o.reserve_url || s.reserve_url || `${base}/offers`,
        image: o.image ? absUrl(s, o.image) : undefined,
        offeredBy: { '@type': 'Resort', '@id': `${base}/#resort`, name: s.site_name || SITE_NAME },
        availability: 'https://schema.org/InStock',
      }),
    })),
  });
};

export const faqSchema = (faqs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.filter((f) => f.question && f.answer).map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
});

/** Remove undefined / empty values so the JSON-LD validates cleanly. */
export function prune(obj) {
  if (Array.isArray(obj)) return obj.map(prune).filter((v) => v !== undefined && v !== null && v !== '');
  if (obj && typeof obj === 'object') {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      const pv = prune(v);
      if (pv === undefined || pv === null || pv === '' || (Array.isArray(pv) && pv.length === 0)) return;
      out[k] = pv;
    });
    return out;
  }
  return obj;
}
